<template>
	<span :class="classes">
		<!-- @see https://primevue.org/calendar -->
		<Calendar
			:disabled="disabled"
			:model-value="value"
			:min-date="minDate"
			:max-date="maxDate"
			:disabled-days="disabledDays"
			:disabled-dates="disabledDates"
			@dateSelect="onChange"
			@hide="onHide"
			@monthChange="onMonthChange"
			@show="onShow"
			@yearChange="onYearChange"
		/>
		<FontAwesomeIcon
			v-if="!isValid"
			class="teaming-calendar-picker-error-icon"
			:icon="[ 'fas', 'exclamation-circle' ]"
		/>
	</span>
</template>

<script>
import { DateTime } from "luxon";

import {
	isString,
	luxonext
} from "@teaming/common-util";

export default {
	name: "TeamingCalendarPicker",
	emits: [ "change", ],
	props: {
		borderless: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		// TODO: should disabledDays be day names?
		// TODO: should disabledDays be combined with filterCriteria?
		disabledDays: {
			type: Array, // Array.<Number>, 0 is Sunday, 1 is Monday...
			default: () => ( [] ),
		},
		filterCriteria: {
			type: Object,
			default: () => ( {
				ordinal: 0,
				weekday: 0,
			} ),
		},
		isValid: {
			type: Boolean,
			default: true,
		},
		maxDate: {
			type: Date,
			default: null,
		},
		minDate: {
			type: Date,
			default: null,
		},
		timezone: {
			type: String,
			default: "local",
		},
		value: {
			type: [ Date, String, ],
			default: undefined,
		},
	},
	data() {
		return {
			isOpen: false,
			calYear: 0,
			calMonth: 0,
		};
	},
	computed: {
		classes() {
			return {
				"teaming-calendar-picker": true,
				"teaming-calendar-picker--borderless": this.borderless,
				"teaming-calendar-picker--focus--invalid": this.isOpen && !this.isValid,
				"teaming-calendar-picker--focus--valid": this.isOpen && this.isValid,
				"teaming-calendar-picker--disabled": this.disabled,
				"teaming-calendar-picker--invalid": !this.isOpen && !this.isValid,
			};
		},
		disabledDates() {
			const { filterCriteria, calMonth, calYear, timezone, } = this;

			if ( !( filterCriteria.ordinal && filterCriteria.weekday ) ) {
				return [];
			}

			const { unmatched, } = luxonext.nthWeekdayOfMonth( {
				nth: filterCriteria.ordinal,
				weekday: filterCriteria.weekday,
				month: calMonth,
				year: calYear,
				timezone,
			} );

			return unmatched.map( d => d.toJSDate() );
		},
	},
	methods: {
		onChange( date ) {
			this.$emit( "change", date );
		},
		onShow() {
			this.isOpen = true;
		},
		onHide() {
			this.isOpen = false;
		},
		onMonthChange( e ) {
			// NOTE: PrimeVue uses the 0-11 month scheme
			// eslint-disable-next-line no-magic-numbers
			this.calMonth = e.month + 1;
		},
		onYearChange( e ) {
			this.calYear = e.year;
		},
	},
	async mounted() {
		const { value, } = this;
		let date;
		if ( isString( value ) ) {
			// TODO: assuming the format here...
			date = DateTime.fromFormat( "yyyy-MM-dd", value );
		} else {
			date = DateTime.fromJSDate( value );
		}
		this.calMonth = date.month;
		this.calYear = date.year;
	},
};
</script>

<style lang="scss" scoped>
.teaming-calendar-picker {
	border: 0.0625rem solid $teaming-calendar-picker-border-color;
	background-color: $teaming-calendar-picker-background-color;
	border-radius: 0.5rem;
	width: 100%;
	display: flex;

	&:not(.teaming-calendar-picker--disabled):hover {
		border: 0.0625rem solid $teaming-calendar-picker-border-color--hover;
	}
}

.teaming-calendar-picker--focus--valid {
	border: 0.0625rem solid $teaming-calendar-picker-border-color--hover;
	box-shadow: 0rem 0rem 0rem .1875rem $teaming-calendar-picker-box-shadow-color--focus,
		0rem .1875rem .4375rem rgba(67, 71, 92, 0.1),
		0rem .3125rem .5rem -0.0625rem rgba(42, 47, 69, 0.05);
}

.teaming-calendar-picker--focus--invalid {
	border-color: $teaming-calendar-picker-border-color--invalid;
	box-shadow: 0rem 0rem 0rem .1875rem $teaming-calendar-picker-box-shadow-color--invalid,
		0rem .1875rem .4375rem rgba(67, 71, 92, 0.1),
		0rem .3125rem .5rem -0.0625rem rgba(42, 47, 69, 0.05);

	&:hover {
		border-color: $teaming-calendar-picker-border-color--invalid--hover;
	}
}

.teaming-calendar-picker--invalid {
	border-color: $teaming-calendar-picker-border-color--invalid;

	&:hover {
		border-color: $teaming-calendar-picker-border-color--invalid--hover;
	}
}

.teaming-calendar-picker--borderless {
	border: none;
}

.teaming-calendar-picker--disabled {
	background-color: $teaming-calendar-picker-background-color--disabled;
	cursor: not-allowed;
}

.teaming-calendar-picker-error-icon {
	float: right;
	align-self: center;
	margin-right: 1rem;
	color: $teaming-calendar-picker-error-icon-color;
}
</style>

<style lang="scss">
.teaming-calendar-picker--disabled .p-component {
	cursor: not-allowed;
}
</style>
